import React, { useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Combobox, Group, Pill, PillsInput, useCombobox } from '@mantine/core';
import { t } from 'i18next';
import { IFeature } from '../../../global/entities/Feature/Feature';

export type IFeatureMultiSelectProps = {
    value?: number[];
    onChange: (values: number[]) => void;
    isError: boolean;
    featuresData?: IFeature[];
    placeholder?: string;
};

const FeatureMultiSelect: React.FC<IFeatureMultiSelectProps> = inject()(
    observer(({ value = [], onChange, isError, featuresData = [], placeholder }) => {
        const [search, setSearch] = useState('');

        const combobox = useCombobox({
            onDropdownClose: () => combobox.resetSelectedOption(),
            onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
        });

        const handleToggleFeature = (featureId: number) => {
            if (value.includes(featureId))
                onChange(value.filter(id => id !== featureId));
            else
                onChange([...value, featureId]);
        };

        const handleValueRemove = (val: string) => {
            onChange(value.filter(id => id !== parseInt(val)));
        };

        const filteredOptions = useMemo(() => {
            return featuresData.filter(feature =>
                feature.name.toLowerCase().includes(search.toLowerCase()) ||
                feature.code.toLowerCase().includes(search.toLowerCase())
            );
        }, [featuresData, search]);

        const options = filteredOptions.map((item) => {
            const isSelected = value.includes(item.id);
            return (
                <Combobox.Option value={item.id.toString()} key={item.id}>
                    <Group gap="sm">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            style={{ pointerEvents: 'none' }}
                        />
                        <span>{item.name}</span>
                    </Group>
                </Combobox.Option>
            );
        });

        const pills = value.map((selectedId) => {
            const feature = featuresData.find(f => f.id === selectedId);
            if (!feature) return null;

            return (
                <Pill key={selectedId} withRemoveButton onRemove={() => handleValueRemove(selectedId.toString())}>
                    {feature.name}
                </Pill>
            );
        }).filter(Boolean);

        return (
            <Combobox
                store={combobox}
                onOptionSubmit={(val) => {
                    handleToggleFeature(parseInt(val));
                    setSearch('');
                }}
            >
                <Combobox.DropdownTarget>
                    <PillsInput onClick={() => combobox.openDropdown()} error={isError}>
                        <Pill.Group>
                            {pills}
                            <Combobox.EventsTarget>
                                <PillsInput.Field
                                    placeholder={placeholder}
                                    value={search}
                                    onChange={(event) => {
                                        combobox.updateSelectedOptionIndex();
                                        setSearch(event.currentTarget.value);
                                    }}
                                    onFocus={() => combobox.openDropdown()}
                                    onBlur={() => combobox.closeDropdown()}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Backspace' && search.length === 0) {
                                            event.preventDefault();
                                            if (value.length > 0)
                                                handleValueRemove(value[value.length - 1].toString());
                                        }
                                    }}
                                />
                            </Combobox.EventsTarget>
                        </Pill.Group>
                    </PillsInput>
                </Combobox.DropdownTarget>

                <Combobox.Dropdown>
                    <Combobox.Options>
                        {options.length > 0 ? (
                            options
                        ) : (
                            <Combobox.Empty>{t('formHcf.noFeaturesFound')}</Combobox.Empty>
                        )}
                    </Combobox.Options>
                </Combobox.Dropdown>
            </Combobox>
        );
    }),
);

export default FeatureMultiSelect;
